// React Query
import { useQuery } from "@tanstack/react-query";

// Hooks
import { useLocation } from "@Common/hooks/Location/useLocation";

// Utils
import { and } from "@Common/helpers/sonar";
import { getSocialMediaReviewDetails } from "@DataPages/api/socialMediaReviews/queries";
import { getTransformedReputationData } from "@/DataPages/components/CRM/SocialMediaReviews/helpers";

// Types
import { IReviewDateRange, ReviewDetailsSource } from "@DataPages/types/socialMediaReviews/utils";
import { ITransformedData } from "@DataPages/components/CRM/SocialMediaReviews/interfaces";
import { Nullable } from "@Common/interfaces/types";

type TGetReviewDetailsArgs = {
	commissionCycleId: string;
	dateRange: IReviewDateRange | null;
};

export default function useGetReviewDetailsInRange(
	source: ReviewDetailsSource,
	args: TGetReviewDetailsArgs
): { data: Nullable<ITransformedData>; isLoading: boolean } {
	const { commissionCycleId: selectedCommissionCycleId, dateRange } = args;
	const { startDate, endDate } = dateRange ?? {};
	const location = useLocation();
	const { locationId, timezone } = location ?? {};

	const { data, isLoading } = useQuery({
		queryKey: ["reputationReviewsDetailsData", locationId, selectedCommissionCycleId, startDate, endDate, source],
		queryFn: async () => {
			const response = await getSocialMediaReviewDetails(locationId as string, dateRange as IReviewDateRange, timezone as string);
			return getTransformedReputationData(response);
		},
		enabled: and(
			Boolean(locationId),
			Boolean(selectedCommissionCycleId),
			Boolean(startDate),
			Boolean(endDate),
			Boolean(source),
			Boolean(timezone)
		),
		staleTime: Infinity,
	});

	return { data, isLoading };
}
